import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, Table, Badge, VehicleImage } from '@/components';
import { useVehicles } from '@/hooks/useProApi';
import { formatVehicle, formatVin, utcToLocal, type VehicleSummary } from '@/types';

type SortOption = 'lastScan' | 'sessionCount' | 'firstScan';

export function Vehicles() {
  const navigate = useNavigate();
  const { data, isLoading } = useVehicles();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('lastScan');

  // Filter and sort vehicles
  const filteredVehicles = useMemo(() => {
    if (!data?.vehicles) return [];

    let vehicles = data.vehicles;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      vehicles = vehicles.filter(v => {
        const vehicleName = formatVehicle(v.vehicleYear, v.vehicleMake, v.vehicleModel).toLowerCase();
        const vin = (v.vin || '').toLowerCase();
        return vehicleName.includes(query) || vin.includes(query);
      });
    }

    // Sort
    vehicles = [...vehicles].sort((a, b) => {
      switch (sortBy) {
        case 'lastScan':
          return utcToLocal(b.lastScanDate).getTime() - utcToLocal(a.lastScanDate).getTime();
        case 'sessionCount':
          return b.sessionCount - a.sessionCount;
        case 'firstScan':
          return utcToLocal(a.firstScanDate).getTime() - utcToLocal(b.firstScanDate).getTime();
        default:
          return 0;
      }
    });

    return vehicles;
  }, [data?.vehicles, searchQuery, sortBy]);

  const columns = [
    {
      key: 'image',
      header: '',
      render: (item: VehicleSummary) => (
        <VehicleImage vin={item.vin} size="sm" />
      ),
    },
    {
      key: 'vehicle',
      header: 'Vehicle',
      render: (item: VehicleSummary) => (
        <div>
          <p className="font-medium">{formatVehicle(item.vehicleYear, item.vehicleMake, item.vehicleModel)}</p>
          <p className="text-gray-400 text-xs font-mono">{formatVin(item.vin)}</p>
        </div>
      ),
    },
    {
      key: 'sessionCount',
      header: 'Sessions',
      render: (item: VehicleSummary) => (
        <Badge variant="default">{item.sessionCount}</Badge>
      ),
    },
    {
      key: 'reportCount',
      header: 'Reports',
      render: (item: VehicleSummary) =>
        item.reportCount > 0 ? (
          <Badge variant="info">{item.reportCount}</Badge>
        ) : (
          <span className="text-gray-500">0</span>
        ),
    },
    {
      key: 'lastScanDate',
      header: 'Last Scan',
      render: (item: VehicleSummary) => format(utcToLocal(item.lastScanDate), 'MMM d, yyyy'),
    },
    {
      key: 'firstScanDate',
      header: 'First Scan',
      render: (item: VehicleSummary) => format(utcToLocal(item.firstScanDate), 'MMM d, yyyy'),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Vehicle Profiles</h1>
        <p className="text-gray-400 mt-1">View all your tracked vehicles and their scan history</p>
      </div>

      {/* Stats */}
      {data?.vehicles && data.vehicles.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="text-center py-4">
            <p className="text-2xl font-bold text-white">{data.vehicles.length}</p>
            <p className="text-gray-400 text-sm">Total Vehicles</p>
          </Card>
          <Card className="text-center py-4">
            <p className="text-2xl font-bold text-white">
              {data.vehicles.reduce((acc, v) => acc + v.sessionCount, 0)}
            </p>
            <p className="text-gray-400 text-sm">Total Sessions</p>
          </Card>
          <Card className="text-center py-4">
            <p className="text-2xl font-bold text-white">
              {data.vehicles.reduce((acc, v) => acc + v.reportCount, 0)}
            </p>
            <p className="text-gray-400 text-sm">Total Reports</p>
          </Card>
          <Card className="text-center py-4">
            <p className="text-2xl font-bold text-white">
              {Math.round(
                data.vehicles.reduce((acc, v) => acc + v.sessionCount, 0) / data.vehicles.length
              )}
            </p>
            <p className="text-gray-400 text-sm">Avg Sessions/Vehicle</p>
          </Card>
        </div>
      )}

      {/* Search and Sort Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by vehicle name or VIN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-10 py-2.5 bg-obdai-primary border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">Sort by:</span>
          <div className="flex rounded-lg border border-gray-700 overflow-hidden">
            {[
              { value: 'lastScan', label: 'Last Scan' },
              { value: 'sessionCount', label: 'Sessions' },
              { value: 'firstScan', label: 'First Scan' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setSortBy(option.value as SortOption)}
                className={`px-3 py-2 text-sm transition ${
                  sortBy === option.value
                    ? 'bg-purple-500/30 text-purple-300'
                    : 'bg-obdai-primary text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Result Count */}
      {data && (
        <p className="text-sm text-gray-400">
          {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
          {searchQuery && data.vehicles && data.vehicles.length !== filteredVehicles.length && (
            <span className="text-gray-500"> (filtered from {data.vehicles.length} total)</span>
          )}
        </p>
      )}

      {/* Vehicles Table */}
      <Card>
        <Table
          columns={columns}
          data={filteredVehicles}
          keyExtractor={(item) => item.vin}
          onRowClick={(item) => navigate(`/vehicles/${item.vin}`)}
          isLoading={isLoading}
          emptyMessage={
            searchQuery
              ? 'No vehicles match your search'
              : 'No vehicles tracked yet. Start scanning with the OBDAI app!'
          }
        />
      </Card>
    </div>
  );
}
